// Retained only so v2 can invalidate records left by the retired v1 runtime.
export const CONTEXTUAL_AUTH_INTENT_STORAGE_KEY = 'gameplaysync.contextualPanelAuthIntent.v1';

export const DEFAULT_CONTEXTUAL_AUTH_INTENT_TTL_MS = 30 * 60 * 1000;

const hasStorageApi = (storage) =>
  storage &&
  typeof storage.getItem === 'function' &&
  typeof storage.setItem === 'function' &&
  typeof storage.removeItem === 'function';

const resolveSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return hasStorageApi(window.sessionStorage) ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const normalizeString = (value) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }

  const normalized = String(value).trim();
  return normalized || null;
};

const normalizeCreatedAt = (createdAt) => {
  const timestamp = Number(createdAt);
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : Date.now();
};

export const CONTEXTUAL_AUTH_INTENT_V2_SCHEMA_VERSION = 2;

export const CONTEXTUAL_AUTH_INTENT_V2_STORAGE_KEY = 'gameplaysync.contextualPanelAuthIntent.v2';

export const CONTEXTUAL_AUTH_INTENT_V2_ACTIONS = Object.freeze({
  REVIEW: 'review',
  REPLY: 'reply',
  REACTION: 'reaction',
  REPORT: 'report',
});

export const CONTEXTUAL_AUTH_INTENT_V2_SURFACES = Object.freeze({
  GAME: 'game',
  ARTICLE: 'article',
});

const CONTEXTUAL_AUTH_INTENT_V2_REACTIONS = new Set(['helpful', 'not_helpful']);
const CONTEXTUAL_AUTH_INTENT_V2_TOP_LEVEL_KEYS = [
  'action',
  'createdAt',
  'expiresAt',
  'returnPath',
  'surface',
  'target',
  'version',
];

const isRecord = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasExactKeys = (value, expectedKeys) => {
  if (!isRecord(value)) {
    return false;
  }

  const actualKeys = Object.keys(value).sort();
  const normalizedExpectedKeys = [...expectedKeys].sort();
  return (
    actualKeys.length === normalizedExpectedKeys.length &&
    actualKeys.every((key, index) => key === normalizedExpectedKeys[index])
  );
};

const getContextualAuthIntentV2TargetKeys = (action, surface) => {
  if (action === CONTEXTUAL_AUTH_INTENT_V2_ACTIONS.REVIEW && surface === CONTEXTUAL_AUTH_INTENT_V2_SURFACES.GAME) {
    return ['gameId', 'gameSlug'];
  }

  if (action === CONTEXTUAL_AUTH_INTENT_V2_ACTIONS.REPLY && surface === CONTEXTUAL_AUTH_INTENT_V2_SURFACES.ARTICLE) {
    return ['articleId', 'articleSlug', 'gameId', 'parentReviewId'];
  }

  if (
    action === CONTEXTUAL_AUTH_INTENT_V2_ACTIONS.REACTION ||
    action === CONTEXTUAL_AUTH_INTENT_V2_ACTIONS.REPORT
  ) {
    const surfaceKeys = surface === CONTEXTUAL_AUTH_INTENT_V2_SURFACES.GAME
      ? ['gameId', 'gameSlug', 'reviewId']
      : surface === CONTEXTUAL_AUTH_INTENT_V2_SURFACES.ARTICLE
        ? ['articleId', 'articleSlug', 'gameId', 'reviewId']
        : null;

    if (!surfaceKeys) {
      return null;
    }

    return action === CONTEXTUAL_AUTH_INTENT_V2_ACTIONS.REACTION
      ? [...surfaceKeys, 'reaction']
      : surfaceKeys;
  }

  return null;
};

export const getContextualAuthIntentV2FallbackPath = (surface) => {
  if (surface === CONTEXTUAL_AUTH_INTENT_V2_SURFACES.GAME) {
    return '/games';
  }
  if (surface === CONTEXTUAL_AUTH_INTENT_V2_SURFACES.ARTICLE) {
    return '/news';
  }
  return '/';
};

const resolveContextualAuthIntentV2ReturnPath = (value) => {
  const normalized = normalizeString(value);
  if (!normalized || normalized.startsWith('//') || normalized.includes('?') || normalized.includes('#')) {
    return null;
  }

  const match = normalized.match(/^\/(games|news)\/([^/]+)$/);
  if (!match) {
    return null;
  }

  let slug;
  try {
    slug = decodeURIComponent(match[2]);
  } catch {
    return null;
  }

  if (!slug || slug === '.' || slug === '..' || /[\\/\s?#]/.test(slug)) {
    return null;
  }

  const canonicalSlug = encodeURIComponent(slug);
  return {
    returnPath: `/${match[1]}/${canonicalSlug}`,
    slug,
    surface: match[1] === 'games'
      ? CONTEXTUAL_AUTH_INTENT_V2_SURFACES.GAME
      : CONTEXTUAL_AUTH_INTENT_V2_SURFACES.ARTICLE,
  };
};

export const normalizeContextualAuthIntentV2ReturnPath = (value, { surface } = {}) => {
  const resolved = resolveContextualAuthIntentV2ReturnPath(value);
  if (!resolved || (surface && resolved.surface !== surface)) {
    return getContextualAuthIntentV2FallbackPath(surface);
  }
  return resolved.returnPath;
};

const normalizeContextualAuthIntentV2Target = ({ action, surface, target, route }) => {
  const expectedKeys = getContextualAuthIntentV2TargetKeys(action, surface);
  if (!expectedKeys || !hasExactKeys(target, expectedKeys)) {
    return null;
  }

  const normalizedTarget = Object.fromEntries(
    expectedKeys.map((key) => [key, normalizeString(target[key])]),
  );
  if (Object.values(normalizedTarget).some((value) => !value)) {
    return null;
  }

  const targetSlug = surface === CONTEXTUAL_AUTH_INTENT_V2_SURFACES.GAME
    ? normalizedTarget.gameSlug
    : normalizedTarget.articleSlug;
  if (targetSlug !== route.slug) {
    return null;
  }

  if (
    action === CONTEXTUAL_AUTH_INTENT_V2_ACTIONS.REACTION &&
    !CONTEXTUAL_AUTH_INTENT_V2_REACTIONS.has(normalizedTarget.reaction)
  ) {
    return null;
  }

  return normalizedTarget;
};

const normalizeContextualAuthIntentV2Ttl = (ttlMs) => {
  const ttl = Number(ttlMs);
  return Number.isFinite(ttl) && ttl > 0
    ? Math.min(ttl, DEFAULT_CONTEXTUAL_AUTH_INTENT_TTL_MS)
    : DEFAULT_CONTEXTUAL_AUTH_INTENT_TTL_MS;
};

export function buildContextualAuthIntentV2({
  action,
  surface,
  returnPath,
  target,
  createdAt,
  ttlMs,
} = {}) {
  const route = resolveContextualAuthIntentV2ReturnPath(returnPath);
  if (!route || route.surface !== surface) {
    return null;
  }

  const normalizedTarget = normalizeContextualAuthIntentV2Target({
    action,
    surface,
    target,
    route,
  });
  if (!normalizedTarget) {
    return null;
  }

  const createdAtMs = normalizeCreatedAt(createdAt);
  return {
    version: CONTEXTUAL_AUTH_INTENT_V2_SCHEMA_VERSION,
    action,
    surface,
    returnPath: route.returnPath,
    createdAt: createdAtMs,
    expiresAt: createdAtMs + normalizeContextualAuthIntentV2Ttl(ttlMs),
    target: normalizedTarget,
  };
}

export const buildReviewAuthIntentV2 = ({
  gameId,
  gameSlug,
  returnPath,
  createdAt,
  ttlMs,
} = {}) => buildContextualAuthIntentV2({
  action: CONTEXTUAL_AUTH_INTENT_V2_ACTIONS.REVIEW,
  surface: CONTEXTUAL_AUTH_INTENT_V2_SURFACES.GAME,
  returnPath,
  createdAt,
  ttlMs,
  target: {
    gameId,
    gameSlug,
  },
});

export const buildReplyAuthIntentV2 = ({
  gameId,
  articleId,
  articleSlug,
  parentReviewId,
  returnPath,
  createdAt,
  ttlMs,
} = {}) => buildContextualAuthIntentV2({
  action: CONTEXTUAL_AUTH_INTENT_V2_ACTIONS.REPLY,
  surface: CONTEXTUAL_AUTH_INTENT_V2_SURFACES.ARTICLE,
  returnPath,
  createdAt,
  ttlMs,
  target: {
    gameId,
    articleId: articleId || articleSlug,
    articleSlug,
    parentReviewId,
  },
});

export const buildReactionAuthIntentV2 = ({
  surface,
  gameId,
  gameSlug,
  articleId,
  articleSlug,
  reviewId,
  reaction,
  returnPath,
  createdAt,
  ttlMs,
} = {}) => buildContextualAuthIntentV2({
  action: CONTEXTUAL_AUTH_INTENT_V2_ACTIONS.REACTION,
  surface,
  returnPath,
  createdAt,
  ttlMs,
  target: surface === CONTEXTUAL_AUTH_INTENT_V2_SURFACES.GAME
    ? { gameId, gameSlug, reviewId, reaction }
    : { gameId, articleId: articleId || articleSlug, articleSlug, reviewId, reaction },
});

export const buildReportAuthIntentV2 = ({
  surface,
  gameId,
  gameSlug,
  articleId,
  articleSlug,
  reviewId,
  returnPath,
  createdAt,
  ttlMs,
} = {}) => buildContextualAuthIntentV2({
  action: CONTEXTUAL_AUTH_INTENT_V2_ACTIONS.REPORT,
  surface,
  returnPath,
  createdAt,
  ttlMs,
  target: surface === CONTEXTUAL_AUTH_INTENT_V2_SURFACES.GAME
    ? { gameId, gameSlug, reviewId }
    : { gameId, articleId: articleId || articleSlug, articleSlug, reviewId },
});

export function normalizeContextualAuthIntentV2(intent, now = Date.now()) {
  if (!hasExactKeys(intent, CONTEXTUAL_AUTH_INTENT_V2_TOP_LEVEL_KEYS)) {
    return null;
  }
  if (intent.version !== CONTEXTUAL_AUTH_INTENT_V2_SCHEMA_VERSION) {
    return null;
  }

  const currentTime = Number(now);
  const createdAt = Number(intent.createdAt);
  const expiresAt = Number(intent.expiresAt);
  const ttlMs = expiresAt - createdAt;
  if (
    !Number.isFinite(currentTime) ||
    !Number.isFinite(createdAt) ||
    !Number.isFinite(expiresAt) ||
    createdAt <= 0 ||
    createdAt > currentTime ||
    ttlMs <= 0 ||
    ttlMs > DEFAULT_CONTEXTUAL_AUTH_INTENT_TTL_MS ||
    expiresAt <= currentTime
  ) {
    return null;
  }

  return buildContextualAuthIntentV2({
    action: intent.action,
    surface: intent.surface,
    returnPath: intent.returnPath,
    target: intent.target,
    createdAt,
    ttlMs,
  });
}

const removeContextualAuthIntentStorageKey = (storage, key) => {
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

export function saveContextualAuthIntentV2(
  intent,
  { storage = resolveSessionStorage(), now = Date.now() } = {},
) {
  if (!hasStorageApi(storage)) {
    return false;
  }

  const normalizedIntent = normalizeContextualAuthIntentV2(intent, now);
  if (!normalizedIntent) {
    clearContextualAuthIntentV2({ storage });
    return false;
  }

  if (!removeContextualAuthIntentStorageKey(storage, CONTEXTUAL_AUTH_INTENT_STORAGE_KEY)) {
    return false;
  }

  try {
    storage.setItem(CONTEXTUAL_AUTH_INTENT_V2_STORAGE_KEY, JSON.stringify(normalizedIntent));
    return true;
  } catch {
    removeContextualAuthIntentStorageKey(storage, CONTEXTUAL_AUTH_INTENT_V2_STORAGE_KEY);
    return false;
  }
}

export function loadContextualAuthIntentV2({
  storage = resolveSessionStorage(),
  now = Date.now(),
} = {}) {
  if (!hasStorageApi(storage)) {
    return null;
  }

  if (!removeContextualAuthIntentStorageKey(storage, CONTEXTUAL_AUTH_INTENT_STORAGE_KEY)) {
    return null;
  }

  let rawValue;
  try {
    rawValue = storage.getItem(CONTEXTUAL_AUTH_INTENT_V2_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!rawValue) {
    return null;
  }

  try {
    const intent = normalizeContextualAuthIntentV2(JSON.parse(rawValue), now);
    if (intent) {
      return intent;
    }
  } catch {
    // Invalid records are cleared below.
  }

  removeContextualAuthIntentStorageKey(storage, CONTEXTUAL_AUTH_INTENT_V2_STORAGE_KEY);
  return null;
}

const matchesContextualAuthIntentV2Expectation = (intent, expected) => {
  if (!expected) {
    return true;
  }
  if (!isRecord(expected)) {
    return false;
  }

  const allowedExpectationKeys = new Set(['action', 'surface', 'returnPath', 'target']);
  if (Object.keys(expected).some((key) => !allowedExpectationKeys.has(key))) {
    return false;
  }

  for (const key of ['action', 'surface', 'returnPath']) {
    if (expected[key] !== undefined && intent[key] !== expected[key]) {
      return false;
    }
  }

  if (expected.target === undefined) {
    return true;
  }
  if (!isRecord(expected.target)) {
    return false;
  }

  return Object.entries(expected.target).every(([key, value]) => (
    Object.prototype.hasOwnProperty.call(intent.target, key) &&
    intent.target[key] === normalizeString(value)
  ));
};

export function consumeContextualAuthIntentV2({
  storage = resolveSessionStorage(),
  now = Date.now(),
  expected,
} = {}) {
  const intent = loadContextualAuthIntentV2({ storage, now });
  if (!intent) {
    return null;
  }

  if (!removeContextualAuthIntentStorageKey(storage, CONTEXTUAL_AUTH_INTENT_V2_STORAGE_KEY)) {
    return null;
  }

  return matchesContextualAuthIntentV2Expectation(intent, expected) ? intent : null;
}

export function clearContextualAuthIntentV2({ storage = resolveSessionStorage() } = {}) {
  if (!hasStorageApi(storage)) {
    return false;
  }

  const clearedV2 = removeContextualAuthIntentStorageKey(storage, CONTEXTUAL_AUTH_INTENT_V2_STORAGE_KEY);
  const clearedV1 = removeContextualAuthIntentStorageKey(storage, CONTEXTUAL_AUTH_INTENT_STORAGE_KEY);
  return clearedV2 && clearedV1;
}
